import { Avatar, Spin, theme } from 'antd'
import dayjs from 'dayjs'
import { MdArrowForward, MdOutlineAttachFile, MdOutlineSettings } from 'react-icons/md'

import { DATETIME_FORMAT } from '@/Ship/Configs/DateFormat'
import { classNames } from '@/Ship/Helpers/classNames'
import { formatDayLabel, initials, userName } from '@/Ship/Helpers/fieldViews'

import type { UserLike } from '@/Ship/Helpers/fieldViews'
import type { CSSProperties, ReactNode } from 'react'

import styles from './HistoryTimeline.module.css'

type Token = ReturnType<typeof theme.useToken>['token']

/**
 * Тон події — колір кружка з іконкою.
 *
 * Кольоровими робимо лише події, що міняють стан документа (створено, взято в
 * роботу, завершено, прострочено, видалено); правки, коментарі й файли —
 * нейтральні. Інакше журнал стає веселкою, у якій нічого не виділяється.
 */
export type HistoryTone = 'blue' | 'geekblue' | 'purple' | 'cyan' | 'green' | 'gold' | 'orange' | 'red' | 'neutral'

export interface HistoryEntry {
    key: string
    /** Що сталося: «Роботу призупинено» */
    title: ReactNode
    icon: ReactNode
    tone: HistoryTone
    /** Хто зробив. `null` — система: планувальник, автоматичні переходи */
    actor?: UserLike | null | undefined
    /** Коли (ISO) */
    occurredAt?: string | undefined
    /** Що саме змінилося */
    details?: ReactNode
}

/**
 * Кольори кружка події за тоном. Спільні з усім, що показує ті самі події
 * поза журналом (стрічка сповіщень), — щоб «завершено» скрізь було тим самим
 * зеленим.
 */
export const toneColors = (token: Token, tone: HistoryTone): { bg: string; fg: string } =>
    tone === 'neutral'
        ? { bg: token.colorFillTertiary, fg: token.colorTextSecondary }
        : { bg: token[`${tone}1`], fg: token[`${tone}6`] }

interface DayGroup {
    key: string
    label: string
    entries: HistoryEntry[]
}

/** Події йдуть так, як їх віддав бек (новіші зверху); групуємо сусідні за днем. */
const groupByDay = (entries: HistoryEntry[]): DayGroup[] => {
    const groups: DayGroup[] = []

    for (const entry of entries) {
        const date = entry.occurredAt ? dayjs(entry.occurredAt) : null
        const key = date?.isValid() ? date.format('YYYY-MM-DD') : 'unknown'
        const last = groups[groups.length - 1]

        if (last?.key === key) {
            last.entries.push(entry)
        } else {
            groups.push({ key, label: date?.isValid() ? formatDayLabel(date) : 'Без дати', entries: [entry] })
        }
    }

    return groups
}

const Actor = ({ actor }: { actor: UserLike | null | undefined }) => {
    const { token } = theme.useToken()

    if (actor === undefined) return null

    if (actor === null) {
        return (
            <span className={classNames(styles['actor'])}>
                <span className={classNames(styles['actorSystem'])}>
                    <MdOutlineSettings />
                </span>
                Система
            </span>
        )
    }

    const name = userName(actor) || 'Користувач'

    return (
        <span className={classNames(styles['actor'])}>
            <Avatar
                size={20}
                style={{ background: token.colorPrimaryBg, color: token.colorPrimary, fontSize: 9, flexShrink: 0 }}
            >
                {initials(name)}
            </Avatar>
            {name}
        </span>
    )
}

export interface HistoryTimelineProps {
    entries: HistoryEntry[]
    loading?: boolean
    emptyText?: string
    /** Під стрічкою: скільки подій не показано */
    note?: ReactNode
}

/**
 * Журнал змін документа.
 *
 * Спільний для карток: одна подія — один рядок осі з іконкою, заголовком,
 * часом, подробицями й автором; дні розділені підписом. Що саме показати в
 * подробицях, вирішує модуль документа — готові цеглинки нижче
 * (`HistoryFields` + `HistoryField`, `HistoryDiff`, `HistoryQuote`).
 */
export const HistoryTimeline = ({ entries, loading = false, emptyText = 'Змін ще не було', note }: HistoryTimelineProps) => {
    const { token } = theme.useToken()

    const palette = {
        '--ht-text': token.colorText,
        '--ht-muted': token.colorTextSecondary,
        '--ht-rail': token.colorSplit,
        '--ht-fill': token.colorFillQuaternary,
        '--ht-accent-soft': token.colorPrimaryBorder,
        '--ht-changed-bg': token.colorPrimaryBg,
        '--ht-changed-fg': token.colorPrimaryText,
        '--ht-added-bg': token.colorSuccessBg,
        '--ht-added-fg': token.colorSuccessText,
        '--ht-removed-bg': token.colorErrorBg,
        '--ht-removed-fg': token.colorErrorText,
    } as CSSProperties

    if (loading) return <Spin style={{ display: 'block', margin: '40px auto' }} />

    if (entries.length === 0) {
        return (
            <div className={classNames(styles['empty'])} style={palette}>
                {emptyText}
            </div>
        )
    }

    return (
        <div className={classNames(styles['timeline'])} style={palette}>
            {groupByDay(entries).map((group) => (
                <section key={group.key}>
                    <div className={classNames(styles['dayLabel'])}>{group.label}</div>
                    <ol className={classNames(styles['list'])}>
                        {group.entries.map((entry) => {
                            const colors = toneColors(token, entry.tone)
                            const time = entry.occurredAt ? dayjs(entry.occurredAt) : null

                            return (
                                <li key={entry.key} className={classNames(styles['item'])}>
                                    <span
                                        className={classNames(styles['icon'])}
                                        style={{ '--ht-icon-bg': colors.bg, '--ht-icon-fg': colors.fg } as CSSProperties}
                                    >
                                        {entry.icon}
                                    </span>
                                    <div className={classNames(styles['body'])}>
                                        <div className={classNames(styles['head'])}>
                                            <span className={classNames(styles['title'])}>{entry.title}</span>
                                            {time?.isValid() ? (
                                                <time
                                                    className={classNames(styles['time'])}
                                                    dateTime={entry.occurredAt}
                                                    title={time.format(DATETIME_FORMAT)}
                                                >
                                                    {time.format('HH:mm')}
                                                </time>
                                            ) : null}
                                        </div>
                                        {entry.details ? (
                                            <div className={classNames(styles['details'])}>{entry.details}</div>
                                        ) : null}
                                        <Actor actor={entry.actor} />
                                    </div>
                                </li>
                            )
                        })}
                    </ol>
                </section>
            ))}
            {note ? <div className={classNames(styles['note'])}>{note}</div> : null}
        </div>
    )
}

// ---- Цеглинки подробиць ------------------------------------------------------

export interface HistoryDiffProps {
    from: ReactNode
    to: ReactNode
    /** Перекреслити старе значення — для тексту й дат; для бейджів і людей ні */
    strike?: boolean
    /** Після нового значення: «+7 дн.» */
    suffix?: ReactNode
}

/** «Було → стало»: старе приглушене. */
export const HistoryDiff = ({ from, to, strike = true, suffix }: HistoryDiffProps) => (
    <span className={classNames(styles['diff'])}>
        <span className={classNames(styles['from'], strike && styles['strike'])}>{from}</span>
        <MdArrowForward className={classNames(styles['arrow'])} aria-label="змінено на" />
        <span>{to}</span>
        {suffix ? <span className={classNames(styles['muted'])}>{suffix}</span> : null}
    </span>
)

/**
 * Табличка змін події: ліворуч поле, праворуч що з ним зроблено.
 *
 * Поле підписане так само, як у картці (дрібно, великими літерами), і стоїть
 * в окремій колонці на сірому тлі — його не сплутати зі значенням. Коли підпис
 * і значення йшли одним рядком, «Опис текст оновлено» читалося як одна фраза.
 */
export const HistoryFields = ({ children }: { children: ReactNode }) => (
    <div className={classNames(styles['fields'])}>{children}</div>
)

/** Що зроблено з полем — мітка перед значенням. */
export type HistoryActionKind = 'changed' | 'added' | 'removed'

const ACTION_LABELS: Record<HistoryActionKind, string> = {
    changed: 'змінено',
    added: 'додано',
    removed: 'знято',
}

export interface HistoryFieldProps {
    /** Назва поля: «Назва», «Спостерігачі» */
    label: ReactNode
    /** Що зроблено; без мітки — значення на момент події (створення, статус) */
    action?: HistoryActionKind
    children?: ReactNode
}

/** Рядок таблички змін: поле │ [що зроблено] значення. */
export const HistoryField = ({ label, action, children }: HistoryFieldProps) => (
    <div className={classNames(styles['field'])}>
        <div className={classNames(styles['fieldLabel'])}>{label}</div>
        <div className={classNames(styles['fieldValue'])}>
            {action ? (
                <span className={classNames(styles['action'], styles[`action-${action}`])}>{ACTION_LABELS[action]}</span>
            ) : null}
            {children}
        </div>
    </div>
)

/** Кілька значень в один рядок із переносом: люди, файли. */
export const HistoryInline = ({ children }: { children: ReactNode }) => (
    <span className={classNames(styles['inline'])}>{children}</span>
)

/** Текст коментаря чи причини — три рядки, далі обрізано. */
export const HistoryQuote = ({ children }: { children: ReactNode }) => (
    <div className={classNames(styles['quote'])}>{children}</div>
)

/** Назва файлу зі скріпкою. */
export const HistoryFile = ({ name }: { name: ReactNode }) => (
    <span className={classNames(styles['file'])}>
        <MdOutlineAttachFile />
        {name}
    </span>
)

/** Приглушений текст: «оновлено», «коментар видалено». */
export const HistoryMuted = ({ children }: { children: ReactNode }) => (
    <span className={classNames(styles['muted'])}>{children}</span>
)
